import React, { useMemo } from 'react';
import { Chip, Stack } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

const STATUS_META = {
  ok: { label: 'OK', color: 'success' },
  shortage: { label: 'Faltante', color: 'warning' },
  priceDiff: { label: 'Dif. precio', color: 'error' },
  promotional: { label: 'Promo', color: 'secondary' },
  discount: { label: 'Descuento', color: 'info' },
  review: { label: 'Revisar', color: 'default' },
};

const getStatusList = (flags = {}) => {
  const ordered = ['shortage', 'priceDiff', 'promotional', 'discount', 'review'];
  const picked = ordered.filter((key) => Boolean(flags[key]));
  if (!picked.length) return ['ok'];
  return picked;
};

const toSafeNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

export default function PaymentInvoiceDetailTable({
  rows = [],
  formatCurrency,
  paginationModel = { page: 0, pageSize: 25 },
  onPaginationModelChange,
}) {
  const columns = useMemo(
    () => [
      {
        field: 'code',
        headerName: 'Codigo',
        minWidth: 110,
        flex: 0.6,
        sortable: false,
      },
      {
        field: 'description',
        headerName: 'Producto / descripcion',
        minWidth: 230,
        flex: 1.7,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
            <span>{params.value || '-'}</span>
            {params.row.rowType === 'bonus' ? (
              <Chip
                label={params.row.bonusKind || 'BONUS'}
                color="secondary"
                size="small"
                sx={{ height: 18, '& .MuiChip-label': { px: 0.6, fontSize: '0.62rem' } }}
              />
            ) : null}
          </Stack>
        ),
      },
      {
        field: 'quantity',
        headerName: 'Cantidad / prometida',
        minWidth: 130,
        flex: 0.55,
        type: 'number',
        valueGetter: (value, row) => toSafeNumber(value ?? row?.quantity ?? row?.qty ?? row?.amount),
      },
      {
        field: 'quantityRe',
        headerName: 'Recibida',
        minWidth: 90,
        flex: 0.55,
        type: 'number',
        valueGetter: (value, row) =>
          toSafeNumber(
            value
            ?? row?.quantityRe
            ?? row?.quantity_re
            ?? row?.received_quantity
            ?? row?.taken_quantity
          ),
      },
      {
        field: 'difference',
        headerName: 'Pendiente / diferencia',
        minWidth: 138,
        flex: 0.55,
        type: 'number',
        valueGetter: (value, row) => {
          const diff = value ?? row?.difference;
          if (diff !== undefined && diff !== null && diff !== '') return toSafeNumber(diff);
          const quantity = toSafeNumber(row?.quantity ?? row?.qty ?? row?.amount);
          const received = toSafeNumber(
            row?.quantityRe
            ?? row?.quantity_re
            ?? row?.received_quantity
            ?? row?.taken_quantity
          );
          return toSafeNumber(quantity - received);
        },
      },
      {
        field: 'prformat',
        headerName: 'Formato',
        minWidth: 120,
        flex: 0.75,
        sortable: false,
      },
      {
        field: 'price',
        headerName: 'Precio factura',
        minWidth: 122,
        flex: 0.8,
        valueGetter: (value, row) =>
          toSafeNumber(
            value
            ?? row?.price
            ?? row?.unit_price
            ?? row?.unitPrice
            ?? row?.invoice_price
          ),
        renderCell: (params) => params.row.rowType === 'bonus' ? '—' : formatCurrency(params.value || 0),
      },
      {
        field: 'orderPrice',
        headerName: 'Precio OC',
        minWidth: 108,
        flex: 0.72,
        valueGetter: (value, row) =>
          toSafeNumber(
            value
            ?? row?.orderPrice
            ?? row?.order_price
            ?? row?.oc_price
            ?? row?.order_detail?.finalPrice
            ?? row?.order_detail?.listPrice
          ),
        renderCell: (params) => {
          if (params.row.rowType === 'bonus') return '—';
          const value = Number(params.value || 0);
          return value > 0 ? formatCurrency(value) : '-';
        },
      },
      {
        field: 'subtotal',
        headerName: 'Subtotal',
        minWidth: 112,
        flex: 0.75,
        valueGetter: (value, row) => {
          const subtotal = value ?? row?.subtotal ?? row?.sub_total ?? row?.line_total;
          if (subtotal !== undefined && subtotal !== null && subtotal !== '') {
            return toSafeNumber(subtotal);
          }
          const quantity = toSafeNumber(row?.quantity ?? row?.qty ?? row?.amount);
          const price = toSafeNumber(row?.price ?? row?.unit_price ?? row?.unitPrice ?? row?.invoice_price);
          return toSafeNumber(quantity * price);
        },
        renderCell: (params) => params.row.rowType === 'bonus' ? '—' : formatCurrency(params.value || 0),
      },
      {
        field: 'status',
        headerName: 'Estado',
        minWidth: 220,
        flex: 1.2,
        sortable: false,
        renderCell: (params) => {
          const statuses = getStatusList(params.row.statusFlags || {});
          const bonusStatus = Number(params.row.pending ?? params.row.difference ?? 0) > 0
            ? { label: 'Pendiente', color: 'warning' }
            : { label: 'Completa', color: 'success' };
          return (
            <Stack direction="row" spacing={0.4} useFlexGap flexWrap="wrap" sx={{ py: 0.25 }}>
              {params.row.rowType === 'bonus' ? (
                <Chip
                  label={bonusStatus.label}
                  color={bonusStatus.color}
                  size="small"
                  variant="outlined"
                  sx={{ height: 18, '& .MuiChip-label': { px: 0.6, fontSize: '0.63rem' } }}
                />
              ) : null}
              {statuses.map((key) => (
                <Chip
                  key={`${params.row.rowKey}:${key}`}
                  label={STATUS_META[key].label}
                  color={STATUS_META[key].color}
                  size="small"
                  variant={key === 'review' ? 'outlined' : 'filled'}
                  sx={{ height: 18, '& .MuiChip-label': { px: 0.6, fontSize: '0.63rem' } }}
                />
              ))}
            </Stack>
          );
        },
      },
    ],
    [formatCurrency]
  );

  return (
    <DataGrid
      rows={rows}
      getRowId={(row) => row.rowKey}
      columns={columns}
      density="compact"
      rowHeight={36}
      pagination
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      pageSizeOptions={[10, 25, 50, 100]}
      disableColumnMenu
      disableColumnFilter
      disableRowSelectionOnClick
      sx={{
        border: 0,
        '& .MuiDataGrid-columnHeaders': {
          bgcolor: 'grey.100',
          borderBottom: '1px solid',
          borderColor: 'divider',
        },
        '& .MuiDataGrid-cell': {
          borderBottomColor: 'divider',
          fontSize: '0.78rem',
        },
        '& .invoice-row-shortage': {
          bgcolor: 'rgba(237, 108, 2, 0.08)',
        },
        '& .invoice-row-price-diff': {
          bgcolor: 'rgba(211, 47, 47, 0.08)',
        },
        '& .invoice-row-promo': {
          bgcolor: 'rgba(156, 39, 176, 0.09)',
        },
        '& .invoice-row-discount': {
          bgcolor: 'rgba(2, 136, 209, 0.06)',
        },
      }}
      getRowClassName={(params) => {
        const flags = params.row.statusFlags || {};
        if (flags.shortage) return 'invoice-row-shortage';
        if (flags.priceDiff) return 'invoice-row-price-diff';
        if (flags.promotional) return 'invoice-row-promo';
        if (flags.discount) return 'invoice-row-discount';
        return '';
      }}
      localeText={{
        noRowsLabel: 'Sin productos para los filtros actuales',
      }}
    />
  );
}
